import { AIR, dcos, dsin } from '@vm/shared';
import { M } from '../palette';
import { MapBuilder, speckle, symPoint, yawToward, type Brush, type DoorSpec } from './builder';
import { stairKeepClear, bunting, boundaryBand, cart, freeGround, G, house, lantern, pieces, pumpkins, Region, scarecrow, scatter, TRIM, well, partition, type Box, type Piece, regionPlay } from './kit';
import { emptyMeta, type MapDef, type MapMeta } from './types';

/**
 * Pumpkin Hollow: a harvest-festival village in the layout of a famous small village arena map (see docs/maps/reference-maps.md): a
 * ring of farm buildings round a central compound with a windmill, a barn and stable to the north, an inn on the west flank, granaries on
 * the east, long lanes between, and a dead-end alley in the south-west. The plan is the reference's (62 x 79 m play area, rescaled so the
 * long side stays under 80 m); everything you see is ours: barn-red, cream, ochre and sage buildings with pumpkin and thatch roofs,
 * furnished rooms, hay carts, bunting, pumpkin heaps, scarecrows and a cornfield boundary.
 * Not mirrored: the plan is asymmetric, so both teams' spawns are placed by hand and checked with tools/map-audit.ts.
 */
const SX = 768;
const SY = 192;
const SZ = 896;
const OX = 74; // play-area origin (voxels)
const OZ = 51;
const PW = 62;
const PD = 79.4;
const px = (x: number): number => OX + Math.round(x * 10);
const pz = (z: number): number => OZ + Math.round(z * 10);
const wx = (x: number): number => OX / 10 + x; // play metres -> world metres
const wz = (z: number): number => OZ / 10 + z;
const WALL_H = 58;

const OUTLINE: [number, number][] = [
  [4.1, 79.4], [2.6, 79.1], [1.6, 68.3], [0.1, 68.0], [2.1, 62.3], [0.0, 62.0], [0.0, 54.2], [1.8, 53.7], [0.0, 52.2], [0.0, 29.3], [8.9, 29.0],
  [11.0, 15.9], [13.7, 15.7], [14.9, 9.1], [30.1, 9.7], [31.9, 7.5], [32.2, 11.5], [37.0, 11.5], [42.0, 8.5], [44.8, 0.1], [48.0, 0.0], [48.1, 11.2],
  [54.1, 11.4], [54.1, 18.2], [56.4, 18.5], [56.3, 27.8], [62.0, 28.1], [61.4, 46.8], [57.6, 49.0], [57.5, 52.9], [43.4, 52.9], [42.1, 56.2],
  [33.9, 55.9], [33.9, 66.0], [8.9, 67.7], [8.6, 76.8],
];
const region = new Region(OUTLINE);

// Palette ---------------------------------------------------------------------------------------
const BARN = M('siding_barn');
const CREAM = M('siding_cream');
const OCHRE = M('siding_ochre');
const SAGE = M('siding_sage');
const CORN: Brush = speckle([M('wheat'), M('hay'), M('wheat'), M('leaves_autumn_1')], 41);
const FALL: Brush = speckle([M('leaves_autumn_1'), M('leaves_autumn_2'), M('leaves_4')], 43);
const GRASS: Brush = speckle([M('grass_4'), M('grass_1'), M('grass_4'), M('grass_2')], 45);
const DIRT: Brush = speckle([M('dirt_1'), M('dirt_3'), M('dirt_2')], 47);
const BUNTING = [M('paint_red'), M('paint_yellow'), M('paint_orange'), M('paint_green'), M('paint_white')];

// Buildings: footprints in play metres from the reference plan ----------------------------------------------------
interface Bld {
  name: string;
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  floors: number;
  wall: number;
  roof: number;
  roofStyle?: 'gable' | 'gableX' | 'flat' | 'pyramid';
  kind: 'barn' | 'inn' | 'hall' | 'mill' | 'store' | 'cottage' | 'shed';
  timber?: boolean;
}
const BUILDINGS: Bld[] = [
  { name: 'barn', x0: 25.4, z0: 12.2, x1: 36.6, z1: 18.6, floors: 2, wall: BARN, roof: M('roof_slate'), roofStyle: 'gable', kind: 'barn' },
  { name: 'stable', x0: 19.6, z0: 13.6, x1: 25.4, z1: 18.6, floors: 1, wall: OCHRE, roof: M('roof_thatch'), roofStyle: 'gableX', kind: 'shed' },
  { name: 'inn', x0: 5.8, z0: 37.6, x1: 17.2, z1: 44.6, floors: 2, wall: CREAM, roof: M('roof_pumpkin'), roofStyle: 'gable', kind: 'inn', timber: true },
  { name: 'kitchen', x0: 10.9, z0: 32.8, x1: 17.2, z1: 37.6, floors: 1, wall: OCHRE, roof: M('roof_thatch'), roofStyle: 'gableX', kind: 'shed' },
  { name: 'hall', x0: 28.3, z0: 28.4, x1: 42.1, z1: 34.6, floors: 2, wall: SAGE, roof: M('roof_red_1'), roofStyle: 'gable', kind: 'hall', timber: true },
  { name: 'millhouse', x0: 31.3, z0: 34.6, x1: 44.0, z1: 40.6, floors: 1, wall: OCHRE, roof: M('roof_pumpkin'), roofStyle: 'gable', kind: 'store' },
  { name: 'windmill', x0: 34.5, z0: 40.6, x1: 40.9, z1: 46.6, floors: 2, wall: CREAM, roof: M('roof_blue'), roofStyle: 'pyramid', kind: 'mill' },
  { name: 'store', x0: 27.1, z0: 48.4, x1: 39.9, z1: 54.2, floors: 1, wall: BARN, roof: M('roof_pumpkin'), roofStyle: 'gable', kind: 'store' },
  { name: 'granary', x0: 48.1, z0: 22.0, x1: 53.7, z1: 29.8, floors: 2, wall: OCHRE, roof: M('roof_slate'), roofStyle: 'gableX', kind: 'store' },
  { name: 'eaststore', x0: 48.1, z0: 29.8, x1: 58.3, z1: 36.9, floors: 2, wall: BARN, roof: M('roof_thatch'), roofStyle: 'gable', kind: 'barn' },
  { name: 'chapel', x0: 48.1, z0: 11.6, x1: 53.9, z1: 18.0, floors: 1, wall: CREAM, roof: M('roof_red_1'), roofStyle: 'gable', kind: 'hall', timber: true },
  { name: 'southeast', x0: 44.0, z0: 45.8, x1: 51.5, z1: 52.5, floors: 1, wall: SAGE, roof: M('roof_pumpkin'), roofStyle: 'gable', kind: 'cottage' },
  { name: 'cottage1', x0: 11.3, z0: 53.9, x1: 29.0, z1: 59.0, floors: 2, wall: SAGE, roof: M('roof_slate'), roofStyle: 'gable', kind: 'cottage' },
  { name: 'cottage2', x0: 9.4, z0: 59.0, x1: 18.2, z1: 65.2, floors: 2, wall: OCHRE, roof: M('roof_pumpkin'), roofStyle: 'gable', kind: 'cottage' },
  { name: 'shed1', x0: 14.0, z0: 23.7, x1: 19.1, z1: 29.6, floors: 1, wall: BARN, roof: M('roof_slate'), roofStyle: 'gableX', kind: 'shed' },
  { name: 'shed2', x0: 5.0, z0: 46.6, x1: 10.7, z1: 51.4, floors: 1, wall: OCHRE, roof: M('roof_thatch'), roofStyle: 'gableX', kind: 'shed' },
];

const inRect = (b: { x0: number; z0: number; x1: number; z1: number }, x: number, z: number, pad = 0): boolean => x > b.x0 - pad && x < b.x1 + pad && z > b.z0 - pad && z < b.z1 + pad;

/** Doors on every side where open ground lies 1.6 m outside the wall (so a door never opens onto a neighbour's wall or the boundary). */
function doorsFor(bd: Bld): DoorSpec[] {
  const cx = (bd.x0 + bd.x1) / 2;
  const cz = (bd.z0 + bd.z1) / 2;
  const sides: [DoorSpec['side'], number, number][] = [
    ['n', cx, bd.z0 - 1.6],
    ['s', cx, bd.z1 + 1.6],
    ['w', bd.x0 - 1.6, cz],
    ['e', bd.x1 + 1.6, cz],
  ];
  const out: DoorSpec[] = [];
  for (const [side, x, z] of sides) {
    if (!region.inside(x, z) || region.edgeDistance(x, z) < 0.6) continue;
    if (BUILDINGS.some((o) => o !== bd && inRect(o, x, z, 0.3))) continue;
    out.push({ side, w: 16, h: 26 });
  }
  if (!out.length) throw new Error(`pumpkin_hollow: ${bd.name} has no open side for a door`);
  return out;
}

/** Furniture for a building's rooms (voxel boxes inside the walls), kept away from the stairs and the doors. */
function furnish(b: MapBuilder, bd: Bld, box: Box, seed: number): void {
  const boxOf = box;
  for (let f = 0; f < bd.floors; f++) {
    const y = G + 30 * f;
    const room = { x0: box.x0 + 2, z0: box.z0 + 2, x1: box.x1 - 2, z1: box.z1 - 2, y };
    // Stairs run along the north wall on even floors and the south wall on odd ones (building()), and open the slab above.
    const avoid = stairKeepClear(boxOf, f);
    const lots: Piece[] = [];
    const up = f > 0;
    switch (bd.kind) {
      case 'barn':
        lots.push(pieces.hayStack(b, y), pieces.hay(b, y), pieces.crate(b, y, 8), pieces.hayStack(b, y), pieces.barrel(b, y, M('paint_orange')), pieces.hay(b, y), pieces.crate(b, y, 10), pieces.hayStack(b, y));
        break;
      case 'inn':
        if (!up) lots.push(pieces.bar(b, y, 40, 8), pieces.table(b, y, 20, 12), pieces.table(b, y, 20, 12), pieces.barrel(b, y, M('paint_brown')), pieces.barrel(b, y, M('paint_brown')), pieces.shelfX(b, y, 24, 22));
        else lots.push(pieces.bed(b, y, M('cloth_red')), pieces.bed(b, y, M('cloth_yellow')), pieces.bed(b, y, M('cloth_green')), pieces.shelfX(b, y, 20, 20), pieces.table(b, y, 14, 10));
        break;
      case 'hall':
        lots.push(pieces.pew(b, y, 34), pieces.pew(b, y, 34), pieces.pew(b, y, 30), pieces.table(b, y, 24, 10), pieces.shelfX(b, y, 24, 22), pieces.pew(b, y, 28));
        break;
      case 'mill':
        lots.push(pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_orange')), pieces.crate(b, y, 8), pieces.hay(b, y));
        break;
      case 'store':
        lots.push(pieces.crate(b, y, 10), pieces.crate(b, y, 8), pieces.barrel(b, y, M('paint_orange')), pieces.shelfX(b, y, 26, 22), pieces.hayStack(b, y), pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_brown')));
        break;
      case 'cottage':
        if (!up) lots.push(pieces.sofa(b, y, M('cloth_red')), pieces.table(b, y, 22, 12), pieces.counter(b, y, 44, 7), pieces.shelfX(b, y, 22, 22), pieces.table(b, y, 16, 10));
        else lots.push(pieces.bed(b, y, M('cloth_blue')), pieces.bed(b, y, M('cloth_yellow')), pieces.shelfX(b, y, 22, 20), pieces.table(b, y, 14, 10));
        break;
      case 'shed':
        lots.push(pieces.crate(b, y, 8), pieces.hay(b, y), pieces.barrel(b, y, M('paint_green')), pieces.shelfX(b, y, 20, 18));
        break;
    }
    scatter(b, room, lots, seed + f * 3, avoid);
  }
}

/** Dirt track along a polyline (play metres), `width` metres wide, only where it lies inside the boundary. */
function track(b: MapBuilder, pts: [number, number][], width: number, mat: Brush): void {
  for (let i = 0; i + 1 < pts.length; i++) {
    const [ax, az] = pts[i];
    const [bx, bz] = pts[i + 1];
    const x0 = Math.min(ax, bx) - width;
    const x1 = Math.max(ax, bx) + width;
    const z0 = Math.min(az, bz) - width;
    const z1 = Math.max(az, bz) + width;
    for (let X = px(x0); X < px(x1); X++)
      for (let Z = pz(z0); Z < pz(z1); Z++) {
        const xm = (X - OX + 0.5) / 10;
        const zm = (Z - OZ + 0.5) / 10;
        if (!region.inside(xm, zm)) continue;
        const dx = bx - ax;
        const dz = bz - az;
        const t = Math.max(0, Math.min(1, ((xm - ax) * dx + (zm - az) * dz) / (dx * dx + dz * dz)));
        const ex = ax + dx * t - xm;
        const ez = az + dz * t - zm;
        if (ex * ex + ez * ez < (width / 2) * (width / 2)) b.set(X, G - 1, Z, mat);
      }
  }
}

const place = (x: number, z: number, r: number, fn: () => void): void => {
  if (region.inside(x, z) && region.edgeDistance(x, z) > r) fn();
};

function build(b: MapBuilder): MapMeta {
  // Ground, then the cornfield boundary ----------------------------------------------------------------------------------------
  b.ground(G, GRASS, speckle([M('dirt_1'), M('dirt_3')], 42), speckle([M('rock_1'), M('rock_2')], 44));
  boundaryBand(b, region, {
    ox: OX, oz: OZ, band: 4, height: WALL_H, skirt: M('planks_dark_1'), skirtH: 9, face: CORN, core: M('rock_1'), cap: CORN,
  });
  // Footprint check: every corner of every building lies inside the boundary and clear of it.
  const bad: string[] = [];
  for (const bd of BUILDINGS)
    for (const [x, z] of [[bd.x0, bd.z0], [bd.x1, bd.z0], [bd.x0, bd.z1], [bd.x1, bd.z1]])
      if (!region.inside(x, z) || region.edgeDistance(x, z) < 0.15) bad.push(`${bd.name} (${x}, ${z})`);
  if (bad.length) throw new Error(`pumpkin_hollow: corners outside or on the boundary: ${bad.join('; ')}`);

  // Tracks: packed dirt between the buildings, and a cobbled ring round the well ----------------------------------------------
  const tracks: [number, number][][] = [
    [[46.4, 1.0], [46.4, 20.0], [41.0, 25.5], [30.0, 25.5], [21.5, 24.5]],
    [[21.5, 24.5], [10.0, 30.0], [3.0, 36.0], [3.0, 62.0], [5.0, 72.0]],
    [[41.0, 25.5], [46.0, 40.0], [60.0, 40.0]],
    [[46.0, 40.0], [46.0, 50.0], [36.0, 58.0], [20.0, 62.0], [5.0, 64.0]],
    [[24.0, 40.0], [30.0, 44.0], [21.5, 24.5]],
  ];
  for (const t of tracks) track(b, t, 2.6, DIRT);
  for (let X = px(14); X < px(29); X++)
    for (let Z = pz(17); Z < pz(32); Z++) {
      const d = Math.hypot((X - OX) / 10 - 21.5, (Z - OZ) / 10 - 24.5);
      if (d < 3.4 && region.inside((X - OX) / 10, (Z - OZ) / 10)) b.set(X, G - 1, Z, (X + Z) % 3 === 0 ? M('cobble_2') : M('cobble_1'));
    }

  // Buildings -----------------------------------------------------------------------------------------------------------------------------
  const boxes = new Map<string, Box>();
  for (const bd of BUILDINGS) {
    const box = house(b, {
      x0: px(bd.x0), z0: pz(bd.z0), x1: px(bd.x1), z1: pz(bd.z1), floors: bd.floors, wall: bd.wall, roof: bd.roof, roofStyle: bd.roofStyle, doors: doorsFor(bd),
      post: M('planks_dark_1'), plinth: M('stone_4'), floor: M('planks_2'), windows: { w: 9, h: 11, sill: 9, spacing: 22 },
      timber: bd.timber ? { mat: M('planks_dark_1'), spacing: 24 } : undefined,
    });
    boxes.set(bd.name, box);
  }
  // Interiors.
  let seed = 3;
  for (const bd of BUILDINGS) furnish(b, bd, boxes.get(bd.name)!, (seed += 7));
  // The inn's tap room: a wall with a doorway between the tap room and the guest end.
  {
    const bx = boxes.get('inn')!;
    partition(b, 0, 'z', bx.x0 + 66, bx.z0 + 2, bx.z1 - 2, (bx.z0 + bx.z1) / 2 - 8);
  }
  // Windmill: a tall landmark. Four sails on the west face, a hub and a cap; the lowest tip stays above head height.
  {
    const bx = boxes.get('windmill')!;
    const hubX = bx.x0 - 3;
    const hubY = G + 78;
    const hubZ = Math.round((bx.z0 + bx.z1) / 2);
    b.box(hubX, hubY - 3, hubZ - 3, hubX + 3, hubY + 3, hubZ + 3, M('planks_dark_2'));
    b.box(hubX + 2, hubY - 1, hubZ - 1, bx.x0, hubY + 1, hubZ + 1, M('planks_dark_2'));
    for (let a = 0; a < 4; a++) {
      const ang = a * (Math.PI / 2) + Math.PI / 4;
      const dy = Math.round(dsin(ang) * 46);
      const dz = Math.round(dcos(ang) * 46);
      b.line(hubX + 1, hubY, hubZ, hubX + 1, hubY + dy, hubZ + dz, M('planks_dark_2'), 2);
      // Sail cloth: a strip beside the arm.
      for (let k = 12; k < 46; k += 2) {
        const sy = hubY + Math.round((dy * k) / 46);
        const sz = hubZ + Math.round((dz * k) / 46);
        const ox = a % 2 === 0 ? 2 : 0;
        const oz = a % 2 === 0 ? 0 : 2;
        b.box(hubX + 1, sy + oz, sz + ox, hubX + 2, sy + oz + 3, sz + ox + 3, M('cloth_white'));
      }
    }
  }

  // The square, the lanes and the farmyards ------------------------------------------------------------------------------------------
  well(b, px(21.5), pz(24.5), M('roof_pumpkin'));
  lantern(b, px(18.0), pz(21.5));
  lantern(b, px(25.0), pz(27.5));
  lantern(b, px(44.4), pz(24.0));
  lantern(b, px(44.0), pz(44.0));
  lantern(b, px(30.0), pz(57.0));
  lantern(b, px(6.0), pz(56.0));
  lantern(b, px(46.4), pz(8.0));
  // Bunting across the square and between the hall and the barn.
  bunting(b, px(18.0), G + 36, pz(21.5), px(25.0), G + 36, pz(27.5), BUNTING, 5);
  bunting(b, px(25.0), G + 36, pz(27.5), px(31.0), G + 40, pz(28.4), BUNTING, 4);
  bunting(b, px(44.4), G + 36, pz(24.0), px(46.4), G + 36, pz(21.0), BUNTING, 3);
  // Wagons: the reference's rotated vehicles are hay carts here.
  place(16.5, 26.5, 2.0, () => cart(b, px(15.5), pz(27.0), false));
  place(8.0, 49.0, 2.0, () => cart(b, px(7.0), pz(50.5), false));
  place(43.0, 21.8, 2.0, () => cart(b, px(41.5), pz(20.5), true));
  place(44.0, 16.7, 1.5, () => cart(b, px(41.0), pz(13.0), true));
  // Cover heaps: pumpkins, hay, crates and low walls.
  place(22.5, 47.5, 1.5, () => pumpkins(b, px(21.5), pz(46.5), 3, 2));
  place(4.1, 56.0, 1.5, () => {
    b.haybale(px(3.0), G, pz(55.0), false);
    b.haybale(px(3.0), G, pz(56.2), false);
    b.haybale(px(3.0), G + 8, pz(55.5), false);
  });
  place(33.0, 26.0, 1.5, () => pumpkins(b, px(32.0), pz(25.0), 2, 2));
  place(40.0, 20.0, 1.5, () => {
    b.haybale(px(38.5), G, pz(19.5), true);
    b.haybale(px(38.5), G + 8, pz(19.5), true);
  });
  place(52.0, 40.0, 1.5, () => pumpkins(b, px(51.0), pz(39.5), 2, 2));
  place(38.0, 53.0, 1.2, () => pumpkins(b, px(36.5), pz(56.5), 2, 1));
  place(44.8, 7.4, 1.2, () => pumpkins(b, px(44.0), pz(7.0), 2, 1));
  place(30.0, 44.0, 2.0, () => b.crate(px(29.0), G, pz(43.0), 10));
  place(26.0, 62.0, 1.5, () => scarecrow(b, px(25.0), pz(61.5)));
  place(52.0, 44.0, 1.5, () => scarecrow(b, px(55.0), pz(43.0)));
  place(8.0, 31.0, 1.5, () => scarecrow(b, px(5.0), pz(31.0)));
  for (const [x, z] of [[27.5, 38.5], [12.5, 49.5], [37.5, 52.0], [40.0, 58.0], [20.0, 33.0]] as [number, number][])
    place(x, z, 2.5, () => {
      if (freeGround(b, px(x) - 2, pz(z) - 2, px(x) + 3, pz(z) + 3)) b.leafyTree(px(x), G, pz(z), 40, 13, M('leaves_autumn_1'));
    });
  place(10.0, 20.0, 3.0, () => b.leafyTree(px(10.0), G, pz(21.0), 44, 14, M('leaves_autumn_2')));
  place(40.0, 14.0, 3.0, () => b.leafyTree(px(41.0), G, pz(14.0), 42, 13, M('leaves_autumn_1')));
  place(4.5, 46.0, 2.0, () => b.leafyTree(px(3.5), G, pz(45.0), 38, 12, M('leaves_autumn_2')));
  place(52.0, 25.0, 1.5, () => b.bush(px(56.5), G, pz(24.0), 5, M('leaves_autumn_2')));
  void FALL;

  // Metadata ----------------------------------------------------------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, wx(PW / 2), wz(PD / 2));
  const mid: [number, number] = [wx(30), wz(38)];
  // Team 0 holds the north-east yard (round the north lane and the chapel); team 1 the west strip and the south-west alley.
  const t0: [number, number][] = [[40.0, 12.6], [42.4, 12.6], [44.6, 12.6], [40.4, 15.8], [42.8, 15.8], [45.0, 15.8], [41.6, 9.4], [44.0, 9.4]];
  const t1: [number, number][] = [[2.6, 58.0], [4.2, 58.0], [2.6, 60.4], [4.2, 60.4], [2.6, 62.8], [4.2, 62.8], [5.2, 70.2], [6.8, 72.6]];
  for (const [x, z] of t0) meta.spawns.push({ team: 0, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  for (const [x, z] of t1) meta.spawns.push({ team: 1, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  const ffa: [number, number][] = [
    [46.4, 3.5], [30.0, 22.0], [22.5, 31.5], [24.0, 12.0], [3.0, 40.0], [2.5, 33.0], [46.0, 36.0], [60.0, 40.5], [58.5, 33.0], [46.5, 49.0], [32.6, 59.4],
    [20.0, 63.0], [5.0, 50.0], [24.0, 44.5], [30.0, 52.0], [53.0, 24.0],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  meta.points.push({ id: 'A', name: 'Well Square', x: wx(21.5), y: gy, z: wz(28.8), r: 3.4 });
  meta.points.push({ id: 'B', name: 'Windmill Yard', x: wx(31.0), y: gy, z: wz(44.0), r: 3.2 });
  meta.points.push({ id: 'C', name: 'Granary Lane', x: wx(46.0), y: gy, z: wz(40.0), r: 3.4 });
  meta.flags.push({ team: 0, x: wx(46.0), y: gy, z: wz(3.5) });
  meta.flags.push({ team: 1, x: wx(4.0), y: gy, z: wz(53.5) });
  const picks: [string, number, number][] = [
    ['health', 46.4, 24.0], ['health', 20.0, 21.0], ['health', 3.0, 46.0], ['health', 30.0, 56.0], ['armor', 30.0, 22.5], ['armor', 24.0, 62.5], ['health', 56.0, 40.0], ['health', 33.0, 43.5],
  ];
  for (const [kind, x, z] of picks) meta.pickups.push({ kind: kind as 'health' | 'armor', x: wx(x), y: b.standAt(wx(x), wz(z), 4.5), z: wz(z) });

  // Keep the voxels around every spawn clear and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  void symPoint;
  void TRIM;
  // Nothing that moves can leave the boundary: an invisible wall at every height along it (the boundary wall is the visible one).
  regionPlay(b, region, OX, OZ, BUILDINGS);
  return meta;
}

export const pumpkinHollow: MapDef = {
  id: 'pumpkin_hollow',
  name: 'Pumpkin Hollow',
  description: 'A harvest-festival village: barns, an inn and a windmill round a well, with a cornfield all round.',
  size: [SX, SY, SZ],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5102,
  version: 2,
  theme: {
    skyTop: 0x78aef0,
    skyHorizon: 0xffe4bc,
    fog: 0xf8dfb8,
    fogNear: 50,
    fogFar: 170,
    sun: [0.5, 0.62, 0.35],
    sunColor: 0xffe6bc,
    sunIntensity: 1.3,
    ambient: 0xfff0dc,
    ambientIntensity: 1.1,
    outside: 0xc4a850,
    music: 'town',
  },
  build,
};
